import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Download, ArrowUpRight } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { PROFILE } from "@/data/content";

const LINKS = [
  { id: "hero", label: "Home", key: NAV.linkHome },
  { id: "about", label: "About", key: NAV.linkAbout },
  { id: "experience", label: "Experience", key: NAV.linkExperience },
  { id: "research", label: "Research", key: NAV.linkResearch },
  { id: "skills", label: "Skills", key: NAV.linkSkills },
  { id: "writing", label: "Writing", key: NAV.linkWriting },
  { id: "faq", label: "FAQ", key: NAV.linkFaq },
  { id: "contact", label: "Contact", key: NAV.linkContact },
];

const HEADER_INITIAL = { y: -80, opacity: 0 };
const HEADER_ANIMATE = { y: 0, opacity: 1 };
const HEADER_TRANSITION = { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 };

const MENU_INITIAL = { height: 0, opacity: 0 };
const MENU_ANIMATE = { height: "auto", opacity: 1 };
const MENU_EXIT = { height: 0, opacity: 0 };
const MENU_TRANSITION = { duration: 0.4, ease: [0.22, 1, 0.36, 1] };

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
    // Attach the scroll listener once. LINKS/setState setters are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (location.pathname !== "/") {
      setActive("");
      return;
    }
    const ids = LINKS.map((l) => l.id);
    const onScroll = () => {
      const line = window.innerHeight * 0.35;
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= line) current = id;
      }
      if (window.innerHeight + window.scrollY >= document.body.scrollHeight - 4) {
        current = ids[ids.length - 1];
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
    // LINKS is a module constant; only pathname changes should re-bind scroll-spy.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) {
      window.__lenis.scrollTo(el, { duration: 1.2 });
    } else {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 450);
    } else {
      // let the mobile menu begin closing, then scroll via Lenis
      setTimeout(() => scrollToId(id), 60);
    }
  };

  const goHome = () => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      return;
    }
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { duration: 1.2 });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <motion.header
      data-testid={NAV.root}
      initial={HEADER_INITIAL}
      animate={HEADER_ANIMATE}
      transition={HEADER_TRANSITION}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled
          ? "bg-[#eef0e9]/80 backdrop-blur-2xl border-b border-[#d2d5ce]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 h-[88px] flex items-center justify-between">
        <button
          data-testid={NAV.logo}
          onClick={goHome}
          className="flex items-center group"
        >
          <img
            src={PROFILE.logo}
            alt="Catherine"
            className="h-14 md:h-16 w-auto object-contain group-hover:opacity-80 transition-opacity duration-300"
          />
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.key}
              onClick={() => go(l.id)}
              className={`nav-link font-mono-plex text-[14px] font-semibold uppercase tracking-[0.12em] link-underline ${
                active === l.id ? "nav-active" : "text-[#48606f] hover:text-[#d14d86]"
              }`}
            >
              {l.label}
            </button>
          ))}
          <a
            data-testid={NAV.downloadCv}
            href="/Catherine-Magawa-CV.pdf"
            download
            className="group flex items-center gap-2 font-mono-plex text-[12px] uppercase tracking-[0.14em] text-[#f5f6f2] bg-[#16324f] px-4 py-2.5 rounded-full hover:bg-[#b5423a] transition-colors duration-400"
          >
            <Download size={13} strokeWidth={1.8} />
            Download CV
          </a>
        </nav>

        <button
          data-testid={NAV.menuToggle}
          onClick={() => setOpen((o) => !o)}
          className="md:hidden flex flex-col gap-[5px] p-2"
          aria-label="Menu"
        >
          <span className={`w-6 h-[1.5px] bg-[#16324f] transition-transform duration-300 ${open ? "translate-y-[6.5px] rotate-45" : ""}`} />
          <span className={`w-6 h-[1.5px] bg-[#16324f] transition-opacity duration-300 ${open ? "opacity-0" : ""}`} />
          <span className={`w-6 h-[1.5px] bg-[#16324f] transition-transform duration-300 ${open ? "-translate-y-[6.5px] -rotate-45" : ""}`} />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={MENU_INITIAL}
            animate={MENU_ANIMATE}
            exit={MENU_EXIT}
            transition={MENU_TRANSITION}
            className="md:hidden overflow-hidden bg-[#eef0e9]/95 backdrop-blur-2xl border-b border-[#d2d5ce]"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`${l.key}-mobile`}
                  onClick={() => go(l.id)}
                  className="flex items-center justify-between font-serif-display text-2xl text-[#16324f]"
                >
                  {l.label}
                  <ArrowUpRight size={20} className="text-[#b5423a]" />
                </button>
              ))}
              <a
                href="/Catherine-Magawa-CV.pdf"
                download
                className="mt-2 flex items-center justify-center gap-2 font-mono-plex text-[12px] uppercase tracking-[0.14em] text-[#f5f6f2] bg-[#16324f] px-4 py-3 rounded-full"
              >
                <Download size={14} /> Download CV
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
